import type { ExpressCheckoutElementOptions, PaypercutElementsAppearance } from '../types/elements';
import { SDK_VERSION } from '../version';
import { requestDeveloperAssistance } from './developer-assistant-loader';
import type { DeveloperCardPreset } from './developer-protocol';

export type DeveloperAssistantComponentDisplayOptions = Pick<
  ExpressCheckoutElementOptions,
  'paymentMethods' | 'layout' | 'buttonType' | 'buttonTheme' | 'buttonHeight' | 'buttonRadius'
>;

export type ResolvedElementsAppearance = PaypercutElementsAppearance & {
  theme: 'light' | 'dark';
  inputs: 'condensed' | 'spaced';
  labels: 'auto' | 'above' | 'floating';
};

export interface DeveloperAssistantPreparedAppearance {
  isCurrent(): boolean;
  commit(): void;
  rollback(): Promise<void>;
}

export type DeveloperAssistantCardValue = Readonly<{
  number: string;
  expirationMonth: number;
  expirationYear: number;
  cvc: string;
}>;

export interface DeveloperAssistantTarget {
  id: string;
  configurationIdentity: string;
  appearanceScopeId: string;
  componentDisplayScopeId?: string;
  presets?: DeveloperCardPreset[];
  presetSource?: 'split-card';
  isVisible(): boolean;
  isAvailable(): boolean;
  isAppearanceAvailable(): boolean;
  appearancePreviewClearsTestData?(): boolean;
  didLastAppearancePreviewClearTestData?(): boolean;
  canPreview?(): boolean;
  applyPreset?(presetId: string): Promise<void>;
  applyCardValue?(value: DeveloperAssistantCardValue): Promise<void>;
  getAppearance(): ResolvedElementsAppearance;
  updateAppearance(appearance: PaypercutElementsAppearance): Promise<void>;
  replaceAppearance?(appearance: PaypercutElementsAppearance): Promise<void>;
  prepareAppearance?(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance>;
  prepareAppearanceReplacement?(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance>;
  getComponentDisplayOptions?(): DeveloperAssistantComponentDisplayOptions;
  updateComponentDisplayOptions?(options: DeveloperAssistantComponentDisplayOptions): Promise<void>;
  replaceComponentDisplayOptions?(
    options: DeveloperAssistantComponentDisplayOptions,
  ): Promise<void>;
  restoreInitialState?(): Promise<void>;
  resetToThemeDefaults?(): Promise<void>;
}

export interface DeveloperAssistantRegistration {
  unregister(): void;
  refresh(): void;
  collapse(): void;
}

type DeveloperAssistantProvider = {
  version: string;
  register(target: DeveloperAssistantTarget): DeveloperAssistantRegistration;
};

type RegistrationEntry = {
  target: DeveloperAssistantTarget;
  delegate: DeveloperAssistantRegistration | null;
  registered: boolean;
};

type DeveloperAssistantRegistry = {
  coreVersions: Set<string>;
  provider: DeveloperAssistantProvider | null;
  registrations: Set<RegistrationEntry>;
};

const registryKey = Symbol.for('paypercut.checkout-js.developer-assistance');

function getRegistry(): DeveloperAssistantRegistry {
  const globals = globalThis as unknown as {
    [key: symbol]: DeveloperAssistantRegistry | undefined;
  };
  globals[registryKey] ??= {
    coreVersions: new Set<string>(),
    provider: null,
    registrations: new Set<RegistrationEntry>(),
  };
  return globals[registryKey];
}

export function announceDeveloperAssistantCore(): void {
  const registry = getRegistry();
  if (registry.provider && registry.provider.version !== SDK_VERSION) {
    for (const entry of registry.registrations) {
      safelyUnregister(entry);
    }
    registry.provider = null;
  }
  registry.coreVersions.add(SDK_VERSION);
}

function safelyUnregister(entry: RegistrationEntry): void {
  try {
    entry.delegate?.unregister();
  } catch {
    // Developer Assistance is optional and must not interrupt owner teardown.
  }
  entry.delegate = null;
}

function safelyAttach(
  entry: RegistrationEntry,
  provider: DeveloperAssistantProvider,
): void {
  if (!entry.registered || entry.delegate) return;
  try {
    entry.delegate = provider.register(entry.target);
  } catch {
    // A target can register before its hosted component is ready. Keep the
    // provider available for the other targets and retry this one on refresh.
  }
}

function isTargetVisible(entry: RegistrationEntry): boolean {
  try {
    return entry.target.isVisible?.() ?? true;
  } catch {
    // Developer Assistance is optional. A stale owner node must never make the
    // merchant page fail while its visibility is being reconciled.
    return false;
  }
}

function syncAttachment(entry: RegistrationEntry, registry: DeveloperAssistantRegistry): void {
  if (!entry.registered) return;
  if (!isTargetVisible(entry)) {
    safelyUnregister(entry);
    return;
  }

  if (registry.provider?.version === SDK_VERSION) {
    safelyAttach(entry, registry.provider);
    return;
  }
  requestDeveloperAssistance(() => hasAttachedDeveloperAssistant(registry));
}

let visibilityObserver: MutationObserver | null = null;

function observeTargetVisibility(registry: DeveloperAssistantRegistry): void {
  if (
    visibilityObserver ||
    typeof MutationObserver === 'undefined' ||
    typeof document === 'undefined' ||
    !document.documentElement
  ) {
    return;
  }
  visibilityObserver = new MutationObserver(() => {
    for (const entry of registry.registrations) syncAttachment(entry, registry);
  });
  visibilityObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
    childList: true,
    subtree: true,
  });
}

function stopObservingTargetVisibilityIfIdle(registry: DeveloperAssistantRegistry): void {
  if (registry.registrations.size > 0) return;
  visibilityObserver?.disconnect();
  visibilityObserver = null;
}

function hasAttachedDeveloperAssistant(registry: DeveloperAssistantRegistry): boolean {
  if (registry.provider?.version !== SDK_VERSION) return false;
  return Array.from(registry.registrations).some(
    (entry) => entry.registered && entry.delegate !== null,
  );
}

export function registerDeveloperAssistantTarget(
  target: DeveloperAssistantTarget,
): DeveloperAssistantRegistration {
  const registry = getRegistry();
  announceDeveloperAssistantCore();
  const entry: RegistrationEntry = { target, delegate: null, registered: true };
  registry.registrations.add(entry);
  observeTargetVisibility(registry);
  syncAttachment(entry, registry);

  return {
    unregister() {
      if (!entry.registered) return;
      entry.registered = false;
      registry.registrations.delete(entry);
      safelyUnregister(entry);
      stopObservingTargetVisibilityIfIdle(registry);
    },
    refresh() {
      if (!entry.registered) return;
      syncAttachment(entry, registry);
      entry.delegate?.refresh();
    },
    collapse() {
      if (entry.registered) entry.delegate?.collapse();
    },
  };
}

export function installDeveloperAssistantProvider(provider: DeveloperAssistantProvider): void {
  const registry = getRegistry();
  const incompatibleCoreVersion = Array.from(registry.coreVersions).find(
    (version) => version !== provider.version,
  );
  if (incompatibleCoreVersion) {
    throw new Error(
      `[Paypercut Elements] Developer Assistance ${provider.version} cannot register with SDK ${incompatibleCoreVersion}.`,
    );
  }
  if (registry.provider) {
    if (registry.provider.version === provider.version) return;
    throw new Error(
      `[Paypercut Elements] Developer Assistance ${provider.version} cannot replace ${registry.provider.version}.`,
    );
  }

  registry.provider = provider;
  for (const entry of registry.registrations) {
    syncAttachment(entry, registry);
  }
}
